import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Monitor, Plus } from "lucide-react";
import { ITTicketStats } from "../components/it-tickets/ITTicketStats";
import { ITTicketsTable } from "../components/it-tickets/ITTicketsTable";
import { CreateITTicketModal } from "../components/it-tickets/CreateITTicketModal";
import { ITTicketDetailModal } from "../components/it-tickets/ITTicketDetailModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusLabels = {
  NUEVA: "Nueva",
  EN_TRATAMIENTO: "En Tratamiento",
  PENDIENTE_PROVEEDOR: "Pend. Proveedor",
  PENDIENTE_CLIENTE: "Pend. Cliente",
  RESUELTO: "Resuelto",
};

const statusColors = {
  NUEVA: "bg-blue-500",
  EN_TRATAMIENTO: "bg-yellow-500",
  PENDIENTE_PROVEEDOR: "bg-orange-500",
  PENDIENTE_CLIENTE: "bg-purple-500",
  RESUELTO: "bg-green-500",
};

const priorityLabels = {
  BAJA: "Baja",
  MEDIA: "Media",
  ALTA: "Alta",
  CRITICA: "Crítica",
};

const priorityColors = {
  BAJA: "bg-slate-500",
  MEDIA: "bg-blue-500",
  ALTA: "bg-orange-500",
  CRITICA: "bg-red-500",
};

const typeLabels = {
  INCIDENCIA: "Incidencia",
  SOLICITUD: "Solicitud",
};

const typeColors = {
  INCIDENCIA: "bg-red-500/20 text-red-400 border-red-500/30",
  SOLICITUD: "bg-blue-500/20 text-blue-400 border-blue-500/30",
};

const ITTicketsPage = () => {
  const { user, canCreate, canEdit, canDelete } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ nueva: 0, en_tratamiento: 0, resuelto: 0, total_open: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [assignedFilter, setAssignedFilter] = useState("all");
  const [supportUsers, setSupportUsers] = useState([]); // Usuarios con rol Soporte
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  
  // Modals
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  
  // New ticket form
  const [newTicket, setNewTicket] = useState({
    ticket_type: "INCIDENCIA",
    title: "",
    description: "",
    priority: "MEDIA",
    assigned_to_id: "",
  });
  
  // Comment
  const [newComment, setNewComment] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [ticketsRes, statsRes, supportUsersRes, allUsersRes] = await Promise.all([
        axios.get(`${API_URL}/api/it-tickets`),
        axios.get(`${API_URL}/api/it-tickets/stats`),
        axios.get(`${API_URL}/api/users/by-role/SOPORTE`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/users`).catch(() => ({ data: [] })),
      ]);
      setTickets(ticketsRes.data);
      setStats(statsRes.data);
      
      // Use SOPORTE users if available, otherwise show all active users
      const soporteUsers = supportUsersRes.data;
      if (soporteUsers.length > 0) {
        setSupportUsers(soporteUsers);
      } else {
        // Fallback: show all active users
        const activeUsers = allUsersRes.data.filter(u => u.is_active !== false);
        setSupportUsers(activeUsers);
      }
    } catch (error) {
      console.error("Error fetching IT tickets:", error);
      toast.error("Error al cargar tickets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Deep-link: open specific IT ticket detail when ?open=<id> is present
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || tickets.length === 0 || detailOpen) return;
    const target = tickets.find((t) => t.id === openId);
    if (target) {
      openDetail(target);
      navigate("/it-support", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, tickets]);

  const handleCreateTicket = async () => {
    if (!newTicket.title.trim() || !newTicket.description.trim()) {
      toast.error("Título y descripción son obligatorios");
      return;
    }

    try {
      const payload = {
        ...newTicket,
        assigned_to_id: newTicket.assigned_to_id || null,
      };
      await axios.post(`${API_URL}/api/it-tickets`, payload);
      toast.success("Ticket creado correctamente");
      setCreateOpen(false);
      setNewTicket({
        ticket_type: "INCIDENCIA",
        title: "",
        description: "",
        priority: "MEDIA",
        assigned_to_id: "",
      });
      fetchData();
    } catch (error) {
      console.error("Error creating ticket:", error);
      toast.error(error.response?.data?.detail || "Error al crear ticket");
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      const res = await axios.put(`${API_URL}/api/it-tickets/${ticketId}`, { status: newStatus });
      const updatedTicket = res.data;
      toast.success("Estado actualizado");
      
      // Update local state immediately for real-time UI
      setSelectedTicket(updatedTicket);
      setTickets(prevTickets => 
        prevTickets.map(t => t.id === ticketId ? { ...t, ...updatedTicket } : t)
      );
      
      // Refresh stats
      const statsRes = await axios.get(`${API_URL}/api/it-tickets/stats`);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar estado");
    }
  };

  const handleAssignTicket = async (ticketId, assigneeId) => {
    try {
      const res = await axios.put(`${API_URL}/api/it-tickets/${ticketId}`, { assigned_to_id: assigneeId || null });
      const updatedTicket = res.data;
      toast.success(assigneeId ? "Ticket asignado" : "Asignación eliminada");
      
      // Update local state immediately for real-time UI
      setSelectedTicket(updatedTicket);
      setTickets(prevTickets => 
        prevTickets.map(t => t.id === ticketId ? { ...t, ...updatedTicket } : t)
      );
    } catch (error) {
      console.error("Error assigning ticket:", error);
      toast.error("Error al asignar ticket");
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTicket) return;

    try {
      const res = await axios.post(`${API_URL}/api/it-tickets/${selectedTicket.id}/comments`, {
        content: newComment,
      });
      const updatedTicket = res.data;
      toast.success("Comentario agregado");
      setNewComment("");
      
      // Update local state immediately for real-time UI
      setSelectedTicket(updatedTicket);
      setTickets(prevTickets => 
        prevTickets.map(t => t.id === selectedTicket.id ? { ...t, ...updatedTicket } : t)
      );
    } catch (error) {
      toast.error("Error al agregar comentario");
    }
  };

  const handleDeleteTicket = async () => {
    if (!ticketToDelete) return;
    
    try {
      await axios.delete(`${API_URL}/api/it-tickets/${ticketToDelete.id}`);
      toast.success("Ticket eliminado correctamente");
      setDeleteDialogOpen(false);
      setTicketToDelete(null);
      setDetailOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error deleting ticket:", error);
      toast.error(error.response?.data?.detail || "Error al eliminar ticket");
    }
  };

  const openDeleteDialog = (e, ticket) => {
    e.stopPropagation();
    setTicketToDelete(ticket);
    setDeleteDialogOpen(true);
  };

  const openDetail = async (ticket) => {
    try {
      const res = await axios.get(`${API_URL}/api/it-tickets/${ticket.id}`);
      setSelectedTicket(res.data);
      setDetailOpen(true);
    } catch (error) {
      toast.error("Error al cargar detalles del ticket");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredTickets = tickets.filter((ticket) => {
    const matchSearch = ticket.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.ticket_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = typeFilter === "all" || ticket.ticket_type === typeFilter;
    const matchStatus = statusFilter === "all" || ticket.status === statusFilter;

    // Assigned-user filter — AND-combines with the others.
    // "all"        → pass through.
    // "unassigned" → no id AND no name (covers legacy rows without id).
    // <userId>     → primary match on assigned_to_id. Fallback to name match
    //                for legacy rows where id was never persisted.
    let matchAssigned = true;
    if (assignedFilter === "unassigned") {
      matchAssigned = !ticket.assigned_to_id && !ticket.assigned_to_name;
    } else if (assignedFilter !== "all") {
      if (ticket.assigned_to_id) {
        matchAssigned = ticket.assigned_to_id === assignedFilter;
      } else if (ticket.assigned_to_name) {
        const u = supportUsers.find((su) => su.id === assignedFilter);
        matchAssigned = !!u && ticket.assigned_to_name === u.name;
      } else {
        matchAssigned = false;
      }
    }

    return matchSearch && matchType && matchStatus && matchAssigned;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#42E695]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="it-tickets-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-white flex items-center gap-3">
            <Monitor className="h-8 w-8 text-[#03BEE4]" />
            Tickets Internos
          </h1>
          <p className="text-slate-400 mt-1">
            Gestión de incidencias y solicitudes internas
          </p>
        </div>
        {canCreate("it_tickets") && (
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-[#42E695] text-[#04042C] hover:bg-[#34D399]"
            data-testid="create-it-ticket-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Ticket
          </Button>
        )}
      </div>

      <ITTicketStats
        stats={stats}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        assignedFilter={assignedFilter}
        setAssignedFilter={setAssignedFilter}
        statusLabels={statusLabels}
        supportUsers={supportUsers}
      />

      <ITTicketsTable
        filteredTickets={filteredTickets}
        typeColors={typeColors}
        typeLabels={typeLabels}
        priorityColors={priorityColors}
        priorityLabels={priorityLabels}
        statusColors={statusColors}
        statusLabels={statusLabels}
        formatDate={formatDate}
        openDetail={openDetail}
        canDelete={canDelete}
        openDeleteDialog={openDeleteDialog}
        deleteDialogOpen={deleteDialogOpen}
        setDeleteDialogOpen={setDeleteDialogOpen}
        ticketToDelete={ticketToDelete}
        handleDeleteTicket={handleDeleteTicket}
      />

      <CreateITTicketModal
        createOpen={createOpen}
        setCreateOpen={setCreateOpen}
        newTicket={newTicket}
        setNewTicket={setNewTicket}
        priorityLabels={priorityLabels}
        supportUsers={supportUsers}
        handleCreateTicket={handleCreateTicket}
      />

      <ITTicketDetailModal
        detailOpen={detailOpen}
        setDetailOpen={setDetailOpen}
        selectedTicket={selectedTicket}
        typeColors={typeColors}
        typeLabels={typeLabels}
        priorityColors={priorityColors}
        priorityLabels={priorityLabels}
        statusColors={statusColors}
        statusLabels={statusLabels}
        formatDate={formatDate}
        canEdit={canEdit}
        supportUsers={supportUsers}
        handleAssignTicket={handleAssignTicket}
        handleUpdateStatus={handleUpdateStatus}
        newComment={newComment}
        setNewComment={setNewComment}
        handleAddComment={handleAddComment}
      />
    </div>
  );
};

export default ITTicketsPage;
